import { ChevronDownIcon, ChevronRightIcon, DotsHorizontalIcon } from '@radix-ui/react-icons';
import {
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Fragment, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import sport_classes from '../../enums/sport_classes.json';
import api from '../../lib/api';
import MarkRegistrationPaidDialog from '../dialogs/markRegistrationPaidDialog';
import { SkeletonRow } from '../skeletons/skeletonrow';
import { Button } from '../ui/button';
import { Dialog } from "../ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import DatatableSortButton from './datatableSortButton';

const getSportClassNameById = (id) => {
    const sportClass = sport_classes.find((sport_class) => sport_class.id === id);
    return sportClass ? sportClass.sport_class_name : "Okänd klass"; // Fallback if ID is not found
};

// Helper: return cup id for the class for a given registration
const getCupIdByRunId = (classId, competition) => {
    // Assume competition.classes is an array with class details including cup_id.
    // Find the class in competition.classes that matches the registration's classId
    const classInfo = competition.classes.find(cl => cl.id === classId);
    return classInfo ? classInfo.cup_id : null;
};

const getSportClassNameByRunId = (classId, competition) => {
    const classItem = competition.classes.find((cls) => cls.id === classId);
    return classItem ? getSportClassNameById(classItem.sport_class_id) : "Okänd klass";
};

// Helper: check if bunny (given its ID) is registered in all classes (that are marked as inCup)
// for a specific cup. We assume competition.classes contains classes with a cup_id field.
// This function returns true if all required class IDs (for that cup) appear
// at least once in any registration (for that bunny) that has inCup true and isn’t paid/out.
const isBunnyFullyRegisteredForCup = (bunnyId, cupId, registrations, competition) => {
    // Get all the class IDs for this cup from the competition classes.
    const requiredClassIds = competition.classes
        .filter(cl => cl.cup_id === cupId)
        .map(cl => cl.id);
    // For this bunny, filter registrations that are marked inCup (and not paid/out).
    const bunnyRegClassIds = registrations
        .filter(reg =>
            reg.bunnyId === bunnyId &&
            reg.inCup &&
            !reg.outOfCompetition
        )
        .map(reg => reg.classId);
    // Bunny is fully registered if every required class is included in its registrations.
    return requiredClassIds.every(reqId => bunnyRegClassIds.includes(reqId));
};


const calculatePaidPrice = (registrations, competition) => {
    let total = 0;

    // Fallbacks for competition prices
    const entryPrice = competition.entry_price || 0;
    const lateEntryPrice = competition.late_entry_price || 0;
    const miniEntryPrice = competition.mini_entry_price || 0;
    const oocEntryPrice = competition.ooc_entry_price || 0;
    const cupEntryPrice = competition.cup_entry_price || 0;
    const classTestPrice = competition.class_test_price || 0;

    const addedCupClassIds = new Set();

    const addedCupKeys = new Set();

    registrations.forEach((registration) => {
        if (!registration || !registration.paidAt) return; // Ignore unpaid registrations

        const paidAt = new Date(registration.paidAt);
        const registeredAt = new Date(registration.registeredAt);
        const registrationDate = new Date(competition.registration_date);
        const lateRegistrationDate = new Date(competition.late_registration_date);

        if (
            isNaN(paidAt) ||
            isNaN(registeredAt) ||
            isNaN(registrationDate) ||
            isNaN(lateRegistrationDate)
        ) {
            console.error('Invalid date in registration or competition:', registration, competition);
            return;
        }

        let price = entryPrice;

        // Ignore crossed-out registrations if no new payment is made
        if (registration.crossedOut) {
            const crossedOutDate = new Date(registration.crossedOut);
            if (crossedOutDate <= paidAt) {

                return; // Ignore this registration
            }
        }

        // Determine the price based on registration conditions
        if (!registration.outOfCompetition && registration.late_notice) {
            price = lateEntryPrice;
        } else if (registration.outOfCompetition) {
            price = oocEntryPrice;
        } else if (getSportClassNameByRunId(registration.classId, competition).toLowerCase().includes('klassprövning')) {
            price = classTestPrice;
        } else if (getSportClassNameByRunId(registration.classId, competition).toLowerCase().includes('mini')) {
            price = miniEntryPrice;
        }

        total += price;

        // Then handle the cup fee on a *per-class* basis,
        // excluding duplicates and possibly excluding OOC:
        // if (registration.inCup && !registration.outOfCompetition && competition.cups.length > 0) {
        //     // Only add the cup price if we haven't added it for this class yet
        //     if (!addedCupClassIds.has(registration.classId)) {
        //         total += cupEntryPrice;
        //         addedCupClassIds.add(registration.classId);
        //     }
        // }

        // Now add cup fee for this registration if applicable.
        // (We add the cup fee only once per bunny per cup – and only if the bunny is fully registered for that cup.)
        if (registration.inCup && !registration.outOfCompetition && competition.cups.length > 0) {
            const cupId = getCupIdByRunId(registration.classId, competition);
            if (cupId !== null &&
                isBunnyFullyRegisteredForCup(registration.bunnyId, cupId, registrations, competition)
            ) {
                // Use a composite key so we only add once per bunny for that cup.
                const cupKey = `${registration.bunnyId}_${cupId}`;
                if (!addedCupKeys.has(cupKey)) {
                    total += cupEntryPrice;
                    addedCupKeys.add(cupKey);
                }
            }
        }
    });

    return total;
};

const calculatePriceToPay = (registrations, competition) => {
    let total = 0;

    // Fallbacks for competition prices
    const entryPrice = competition.entry_price || 0;
    const lateEntryPrice = competition.late_entry_price || 0;
    const miniEntryPrice = competition.mini_entry_price || 0;
    const oocEntryPrice = competition.ooc_entry_price || 0;
    const cupEntryPrice = competition.cup_entry_price || 0;
    const classTestPrice = competition.class_test_price || 0;

    const addedCupBunnyIds = new Set();

    registrations.forEach((registration) => {
        if (!registration) return;

        const registeredAt = new Date(registration.registeredAt);
        const registrationDate = new Date(competition.registration_date);
        const lateRegistrationDate = new Date(competition.late_registration_date);
        const paidAt = registration.paidAt ? new Date(registration.paidAt) : null;
        const crossedOutDate = registration.crossedOut ? new Date(registration.crossedOut) : null;

        if (
            isNaN(registeredAt) ||
            isNaN(registrationDate) ||
            isNaN(lateRegistrationDate) ||
            (paidAt && isNaN(paidAt)) ||
            (crossedOutDate && isNaN(crossedOutDate))
        ) {
            console.error('Invalid date in registration or competition:', registration, competition);
            return;
        }

        let price = entryPrice;

        // Determine the price based on registration conditions
        if (crossedOutDate && paidAt && crossedOutDate > paidAt) {
            // If crossed out after payment, subtract the price
            if (!registration.outOfCompetition && registration.late_notice) {
                price = -lateEntryPrice; // Subtract late entry price
            } else if (registration.outOfCompetition) {
                price = -oocEntryPrice; // Subtract out-of-competition price
            } else if (getSportClassNameByRunId(registration.classId, competition).toLowerCase().includes('klassprövning')) {
                price = -classTestPrice;
            } else if (getSportClassNameByRunId(registration.classId, competition).toLowerCase().includes('mini')) {
                price = -miniEntryPrice; // Subtract mini entry price
            } else {
                price = -entryPrice; // Subtract standard entry price
            }

            total += price;

            // PRE
            // if (registration.inCup && !registration.outOfCompetition && !registration.paidAt && competition.cups.length > 0) {
            //     if (!addedCupBunnyIds.has(registration.bunnyId)) {
            //         total += cupEntryPrice;
            //         addedCupBunnyIds.add(registration.bunnyId);
            //     }
            // }

            // Add the cup fee for registrations if eligible
            if (registration.inCup &&
                !registration.outOfCompetition &&
                !registration.paidAt &&
                competition.cups.length > 0) {
                const cupId = getCupIdByRunId(registration.classId, competition);
                if (cupId !== null &&
                    isBunnyFullyRegisteredForCup(registration.bunnyId, cupId, registrations, competition)) {
                    if (!addedCupBunnyIds.has(registration.bunnyId)) {
                        total += cupEntryPrice;
                        addedCupBunnyIds.add(registration.bunnyId);
                    }
                }
            }
            return; // Skip further checks for crossed-out registrations
        }

        // Skip unpaid but crossed-out registrations - they shouldn't contribute to price to pay
        if (crossedOutDate && !paidAt) {
            return;
        }

        if (crossedOutDate) {
            price = 0;
        }

        if (registration.paidAt) {
            price = 0;
        } else if (!registration.outOfCompetition && registration.late_notice) {
            price = lateEntryPrice;
            // price = entryPrice
        } else if (registration.outOfCompetition) {
            price = oocEntryPrice;
        } else if (getSportClassNameByRunId(registration.classId, competition).toLowerCase().includes('klassprövning')) {
            price = classTestPrice;
        } else if (getSportClassNameByRunId(registration.classId, competition).toLowerCase().includes('mini')) {
            price = miniEntryPrice;
        }

        total += price;

        //PRE
        // if (registration.inCup && !registration.outOfCompetition && !registration.paidAt && competition.cups.length > 0) {
        //     if (!addedCupBunnyIds.has(registration.bunnyId)) {
        //         total += cupEntryPrice;
        //         addedCupBunnyIds.add(registration.bunnyId);
        //     }
        // }
        // });

        // For cup fee, add only if the bunny is fully registered in that cup.
        if (registration.inCup &&
            !registration.outOfCompetition &&
            !registration.paidAt &&
            competition.cups.length > 0) {
            const cupId = getCupIdByRunId(registration.classId, competition);
            if (cupId !== null &&
                isBunnyFullyRegisteredForCup(registration.bunnyId, cupId, registrations, competition)) {
                if (!addedCupBunnyIds.has(registration.bunnyId)) {
                    total += cupEntryPrice;
                    addedCupBunnyIds.add(registration.bunnyId);
                }
            }
        }
    });

    return total;
};

// --------------------
// COLUMN DEFINITIONS
// --------------------
const getColumns = (auth, fetchCompetition, fetchParticipants, competition, dataLength) => {
    const isAdminOrPrivilegedUser = auth?.user?.superadmin
        || (auth?.user?.roleInClub === 'admin' && auth?.user?.memberOfClub === parseInt(competition.club.id));

    const columns = [
        {
            // Collapsible expander column
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => row.toggleExpanded()}
                    style={{ cursor: 'pointer' }}
                >
                    {row.getIsExpanded() ? <ChevronDownIcon className='size-4' /> : <ChevronRightIcon className='size-4' />}
                </Button>
            ),
        },
        {
            accessorKey: "firstName",
            header: ({ column }) => (
                <DatatableSortButton column={column}>Förare</DatatableSortButton>
            ),
            cell: (info) => {
                const participant = info.row.original;
                return <span>{`${participant.firstName} ${participant.lastName}`}</span>;
            },
        },
        {
            accessorKey: "clubName",
            header: ({ column }) => (
                <DatatableSortButton column={column}>Förening</DatatableSortButton>
            ),
            cell: (info) => {
                const club = info.row.original;
                return <Link to={`/clubs/${club.clubId}`} className="text-blue-600 font-medium hover:underline">{club.clubName}</Link>;
            },
        },
        {
            accessorKey: "registrations",
            header: ({ column }) => (
                <DatatableSortButton column={column}>Starter</DatatableSortButton>
            ),
            cell: (info) => {
                const participant = info.row.original;
                const registrationCount = participant.registrations.filter(reg => !reg.crossedOut).length;
                return <span>{registrationCount}</span>;
            },
            sortingFn: (rowA, rowB) => {
                const regCountA = rowA.original.registrations.filter(reg => !reg.crossedOut).length;
                const regCountB = rowB.original.registrations.filter(reg => !reg.crossedOut).length;
                return regCountA - regCountB;
            },
        },
        {
            accessorKey: "amountToPay",
            header: "Betalningar",
            cell: (info) => {
                const participant = info.row.original;
                const originalPrice = calculatePaidPrice(participant.registrations, competition);
                const newPrice = calculatePriceToPay(participant.registrations, competition);

                // Only show payment info if it's the current user or if user has admin privileges
                const isOwnData = auth?.user?.id === participant.userId;
                const isAdmin = auth?.user?.superadmin ||
                    (auth?.user?.roleInClub === 'admin' &&
                        auth?.user?.memberOfClub === parseInt(competition.club.id));

                if (isOwnData || isAdmin) {
                    return (
                        <div>
                            <p><span className='font-medium'>Betalt:</span> {originalPrice.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })}</p>
                            {newPrice !== 0 && (
                                <p><span className='font-medium'>Kvar att betala:</span> {newPrice.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })}</p>
                            )}
                        </div>
                    );
                }

                // For other users, don't show payment information
                return
            },
        }
    ];

    if (isAdminOrPrivilegedUser && dataLength > 0) {
        columns.push(
            {
                id: "actions",
                enableHiding: false,
                cell: ({ row }) => {
                    const driver = row.original;
                    return (
                        <Dialog modal={true}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0 float-right">
                                        <span className="sr-only">Öppna meny</span>
                                        <DotsHorizontalIcon className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    onCloseAutoFocus={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    <DropdownMenuLabel>Alternativ</DropdownMenuLabel>
                                    <>
                                        {(auth.user?.superadmin ||
                                            auth?.user?.roleInClub === 'admin') && (
                                                <>
                                                    {driver.registrations.paidAt !== null ? (
                                                        <MarkRegistrationPaidDialog
                                                            driverId={driver.userId}
                                                            competitionId={competition.id}
                                                            fetchCompetition={fetchCompetition}
                                                            fetchParticipants={fetchParticipants}
                                                        />
                                                    ) : null}
                                                </>
                                            )}
                                    </>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </Dialog>
                    );
                },
            }
        );
    }

    return columns;
};

export function CompetitionParticipantsDatatable({ participants, isLoading, fetchCompetition, fetchParticipants, competition }) {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const { auth } = useContext(AuthContext);

    const [expanded, setExpanded] = useState({});
    const [participatingBunnies, setParticipatingBunnies] = useState([]);

    useEffect(() => {
        const fetchParticipatingBunnies = async () => {
            const response = await api.get(
                `https://skhrf-api.cmdigital.se/v1/competitions/${competition.id}/bunnies`
            );
            setParticipatingBunnies(response.data);

        };
        fetchParticipatingBunnies();
    }, [competition.id]);

    const table = useReactTable({
        data: participants,
        columns: getColumns(auth, fetchCompetition, fetchParticipants, competition, participants.length),
        getRowId: (row, index) => row.id || index.toString(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onExpandedChange: setExpanded,
        state: {
            sorting,
            columnFilters,
            expanded,
        },
    });

    return (
        <div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <SkeletonRow columns={getColumns(
                                auth,
                                fetchCompetition,
                                fetchParticipants,
                                competition
                            ).length}
                                rows={participants.length}
                            />
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => {
                                // Perform the filtering here to get the current driver's bunnies
                                const bunniesForThisDriver =
                                    participatingBunnies.find(
                                        (driver) => driver.userId === row.original.userId
                                    )?.bunnies || [];

                                return (
                                    <Fragment key={row.id}>
                                        <TableRow data-state={row.getIsSelected() && "selected"}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {row.getIsExpanded() && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        getColumns(
                                                            auth, fetchCompetition, fetchParticipants, competition, participants.length
                                                        ).length
                                                    }
                                                >
                                                    <ExpandedContent
                                                        participant={row.original}
                                                        bunnies={bunniesForThisDriver}
                                                        competition={competition}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        getColumns(
                                            auth,
                                            fetchCompetition,
                                            fetchParticipants,
                                            competition
                                        ).length
                                    }
                                    className="h-24 text-center"
                                >
                                    Inget hittades.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Simple pagination controls */}
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                    Sida {table.getState().pagination.pageIndex + 1} av {table.getPageCount()}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Föregående
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Nästa
                </Button>
            </div>
        </div>
    );
}

// ------------------------
// EXPANDED CONTENT COMPONENT
// ------------------------
function ExpandedContent({ participant, bunnies, competition }) {

    return (
        <p>
            {participant.firstName} {participant.lastName} är anmäld med följande kaniner:{' '}
            {bunnies.map((bunny, index) => {
                // Define cups INSIDE the map loop, ensuring it's unique per bunny
                const cups = [];
                const bunnyRegistrations = participant.registrations.filter(reg => reg.bunnyId === bunny.bunnyId);

                // Check if all registrations for the bunny in the cup are marked as inCup
                bunnyRegistrations.forEach((registration) => {
                    if (registration.inCup) {
                        const competitionClass = competition.classes.find(cls => cls.id === registration.classId);
                        if (competitionClass?.cup_id) {
                            const cup = competition.cups?.find(cup => cup.id === competitionClass.cup_id);
                            if (cup && !cups.includes(cup.name)) {
                                // Check if all registrations for this cup are inCup
                                const allInCup = bunnyRegistrations.every(reg =>
                                    reg.inCup || competition.classes.find(cls => cls.id === reg.classId)?.cup_id !== cup.id
                                );
                                if (allInCup) {
                                    cups.push(cup.name);
                                }
                            }
                        }
                    }
                });

                return (
                    <span key={bunny.bunnyId}>
                        <br />
                        <Link className={`text-blue-600 font-medium hover:underline`}
                            to={`/bunny/${bunny.bunnyId}`}
                        >
                            {bunny.bunnyName + ' '}
                        </Link>
                        {'('}
                        {bunny.classes.map((cls, idx) => {
                            // Check if any registration for this bunny and this class is crossed out.
                            // Use bunnyRegistrations to ensure we’re only looking at registrations for this bunny.
                            const crossedForThisClass = bunnyRegistrations.some(
                                reg => reg.classId === cls.classId && reg.crossedOut
                            );
                            return (
                                <Fragment key={cls.classId}>
                                    <span className={crossedForThisClass ? 'text-destructive' : ''}>
                                        {cls.className}
                                    </span>
                                    {idx < bunny.classes.length - 1 && ', '}
                                </Fragment>
                            );
                        })}
                        {')'}
                        {cups.length > 0 && (
                            <>
                                {' '}i cup <span className=''>{cups.join(", ")}</span>
                            </>
                        )}
                        {index < bunnies.length - 1 && ', '}
                    </span>
                );
            })}
        </p>
    );
}

